import { DefaultValueConfig } from '@headless-workspace/config';
import { Image } from '@headless-workspace/typings';
import { ObjectHelpers, ValueHelpers } from '@headless-workspace/utils';
import { mapURLToImage } from '../../../client';
import {
    CapacityFormat,
    getPromotionKey,
    mapProductPriceData,
    ProductDetailVariantDTO,
    ProductPriceData,
    PromotionType,
} from '../../../server';

export type ProductDetailVariantValue = ProductPriceData & {
    id: string;
    name?: string;
    image: Image;
    thumbnailImage: Image;
    isAvailable: boolean;
    discountPercentage?: number;
    pricePerUnit?: string;
    promotion?: PromotionType;
    format: CapacityFormat;
    url?: string;
};

export const extractVariantFormats = (variants: ProductDetailVariantValue[]): CapacityFormat[] => {
    const compareAlphabetically = (a: CapacityFormat, b: CapacityFormat) => a.localeCompare(b);
    return Array.from(new Set(variants.map((variant) => variant.format))).sort(compareAlphabetically);
};

export const mapFormat = (format?: string | null) => {
    if (format === 'Mini') {
        return CapacityFormat.Mini;
    }
    return CapacityFormat.Standard;
};

const getImageWithFallback = (url?: string): Image =>
    mapURLToImage(url) ?? ObjectHelpers.createImageWithSrc(DefaultValueConfig.imagePlaceholder);

export const mapProductDetailVariant = (dto: ProductDetailVariantDTO, currency: string): ProductDetailVariantValue => {
    const prices = mapProductPriceData(dto, currency);
    const imageUrl = dto.medias?.[0]?.url;

    return {
        id: dto.id,
        name: ValueHelpers.mapOptional(dto.name),
        url: ValueHelpers.mapOptional(dto.url),
        thumbnailImage:
            mapURLToImage(dto.swatchImageUrl) ?? ObjectHelpers.createImageWithSrc(DefaultValueConfig.imagePlaceholder),
        image: getImageWithFallback(imageUrl),
        isAvailable: dto.isAvailable ?? true,
        format: mapFormat(dto.format),
        promotion: getPromotionKey(dto.promotion, prices),
        pricePerUnit: ValueHelpers.mapOptional(dto.pricePerUnit),
        ...mapProductPriceData(dto, currency),
    };
};
